
import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import tradingSalesInvoiceService from "../services/tradingSalesInvoiceService";

const getResponseData = (response) =>
  response?.data?.data ?? response?.data ?? {};

const normalizeStatus = (value) =>
  String(value || "DRAFT").trim().toUpperCase();

const formatDate = (value) => {
  if (!value) return "—";
  const raw = String(value).slice(0, 10);
  const parts = raw.split("-");
  return parts.length === 3
    ? new Date(`${raw}T00:00:00`).toLocaleDateString("en-GB", {
        day: "2-digit",
        month: "short",
        year: "numeric",
      })
    : raw;
};

const formatAmount = (value) =>
  Number(value || 0).toLocaleString("en-IN", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

const statusColors = {
  DRAFT: { background: "#efedff", color: "#5548cb" },
  PENDING_APPROVAL: { background: "#fff4d8", color: "#9a6200" },
  APPROVED: { background: "#e5f0ff", color: "#1d55a5" },
  REJECTED: { background: "#fee2e2", color: "#b42318" },
  POSTED: { background: "#dcfce7", color: "#166534" },
  CANCELLED: { background: "#f1f3f9", color: "#697184" },
};

const styles = {
  page: {
    padding: 24,
    background: "#f7f8fc",
    minHeight: "100%",
    fontFamily: "Inter, Arial, sans-serif",
    color: "#252b3b",
  },
  header: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    gap: 16,
    flexWrap: "wrap",
    marginBottom: 20,
  },
  title: { margin: 0, fontSize: 25, fontWeight: 750 },
  subtitle: { margin: "7px 0 0", color: "#747b8d", fontSize: 13 },
  button: {
    border: 0,
    borderRadius: 8,
    padding: "11px 17px",
    background: "linear-gradient(110deg, #6254d9, #4737bd)",
    color: "#fff",
    fontWeight: 700,
    cursor: "pointer",
  },
  secondaryButton: {
    border: "1px solid #dfe2eb",
    borderRadius: 8,
    padding: "9px 13px",
    background: "#fff",
    color: "#374151",
    fontWeight: 600,
    cursor: "pointer",
  },
  stats: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(190px, 1fr))",
    gap: 13,
    marginBottom: 17,
  },
  statCard: {
    minHeight: 105,
    borderRadius: 13,
    padding: 18,
    color: "#fff",
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    boxShadow: "0 5px 13px rgba(15,23,42,.10)",
  },
  total: { background: "linear-gradient(115deg, #19a7f5, #0860e8)" },
  draft: { background: "linear-gradient(115deg, #a47bff, #7738df)" },
  pending: { background: "linear-gradient(115deg, #ffb51b, #ff8500)" },
  posted: { background: "linear-gradient(115deg, #39c985, #159957)" },
  statLabel: {
    fontSize: 11,
    fontWeight: 750,
    marginBottom: 8,
    textTransform: "uppercase",
  },
  statValue: { fontSize: 28, fontWeight: 800 },
  statIcon: {
    width: 45,
    height: 45,
    borderRadius: "50%",
    background: "rgba(255,255,255,.22)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: 21,
  },
  summary: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    gap: 12,
    flexWrap: "wrap",
    padding: 15,
    borderRadius: 12,
    background: "linear-gradient(110deg, #eef4ff, #f4f0ff)",
    border: "1px solid #e4e8fa",
    marginBottom: 17,
  },
  card: {
    background: "#fff",
    border: "1px solid #e8eaf2",
    borderRadius: 13,
    padding: 18,
    boxShadow: "0 2px 8px rgba(15,23,42,.025)",
  },
  filters: {
    display: "grid",
    gridTemplateColumns:
      "minmax(220px, 2fr) repeat(3, minmax(140px, 1fr)) auto auto",
    gap: 10,
    alignItems: "end",
    marginBottom: 20,
  },
  field: { display: "flex", flexDirection: "column", gap: 6 },
  label: { color: "#667085", fontSize: 11, fontWeight: 700 },
  input: {
    width: "100%",
    boxSizing: "border-box",
    border: "1px solid #dfe2eb",
    borderRadius: 8,
    padding: "10px 11px",
    background: "#fff",
    fontSize: 12,
    color: "#303747",
  },
  tableTop: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    gap: 12,
    flexWrap: "wrap",
    marginBottom: 12,
  },
  tableWrap: {
    width: "100%",
    maxHeight: "calc(100vh - 430px)",
    minHeight: 220,
    overflow: "auto",
    border: "1px solid #edf0f5",
    borderRadius: 9,
  },
  table: {
    width: "100%",
    minWidth: 1050,
    borderCollapse: "separate",
    borderSpacing: 0,
  },
  th: {
    position: "sticky",
    top: 0,
    zIndex: 2,
    textAlign: "left",
    padding: "13px 12px",
    background: "#f5f6fb",
    color: "#70778a",
    fontSize: 10,
    textTransform: "uppercase",
    borderBottom: "1px solid #e8eaf2",
    whiteSpace: "nowrap",
  },
  td: {
    padding: "13px 12px",
    fontSize: 12,
    borderBottom: "1px solid #f0f1f6",
    whiteSpace: "nowrap",
    background: "#fff",
  },
  link: {
    color: "#5548cb",
    fontWeight: 750,
    cursor: "pointer",
    border: 0,
    padding: 0,
    background: "transparent",
    fontSize: 12,
  },
  badge: {
    display: "inline-block",
    borderRadius: 20,
    padding: "5px 9px",
    fontSize: 10,
    fontWeight: 750,
    whiteSpace: "nowrap",
  },
  action: {
    border: "1px solid #e0e3ec",
    borderRadius: 6,
    padding: "6px 9px",
    background: "#fff",
    color: "#4d49a6",
    fontSize: 11,
    fontWeight: 700,
    cursor: "pointer",
  },
  footer: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    gap: 12,
    flexWrap: "wrap",
    marginTop: 15,
    color: "#747b8d",
    fontSize: 12,
  },
  error: {
    padding: 12,
    background: "#fff0f0",
    color: "#b42318",
    borderRadius: 8,
    marginBottom: 15,
  },
  muted: {
    padding: 35,
    textAlign: "center",
    color: "#858b9b",
  },
};

function StatusBadge({ status }) {
  const code = normalizeStatus(status);
  return (
    <span style={{ ...styles.badge, ...(statusColors[code] || {}) }}>
      {code.replace(/_/g, " ")}
    </span>
  );
}

function StatCard({ label, value, accent, icon }) {
  return (
    <div style={{ ...styles.statCard, ...accent }}>
      <div>
        <div style={styles.statLabel}>{label}</div>
        <div style={styles.statValue}>
          {Number(value || 0).toLocaleString("en-IN")}
        </div>
      </div>
      <div style={styles.statIcon}>
        <i className={icon} />
      </div>
    </div>
  );
}

export default function TradingSalesInvoiceListPage() {
  const navigate = useNavigate();

  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const [searchInput, setSearchInput] = useState("");
  const [statusInput, setStatusInput] = useState("ALL");
  const [fromDateInput, setFromDateInput] = useState("");
  const [toDateInput, setToDateInput] = useState("");

  const [filters, setFilters] = useState({
    search: "",
    status_code: "",
    from_date: "",
    to_date: "",
  });

  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(20);

  const [pagination, setPagination] = useState({
    total_records: 0,
    total_pages: 0,
  });

  const [kpis, setKpis] = useState({
    total: 0,
    draft: 0,
    pending: 0,
    posted: 0,
  });

  const loadList = useCallback(async () => {
    setLoading(true);
    setError("");

    try {
      const params = { page, per_page: perPage };

      if (filters.search) params.search = filters.search;
      if (filters.status_code) params.status_code = filters.status_code;
      if (filters.from_date) params.from_date = filters.from_date;
      if (filters.to_date) params.to_date = filters.to_date;

      const response =
        await tradingSalesInvoiceService.getSalesInvoiceList(params);

      const body = getResponseData(response);

      setRecords(Array.isArray(body.items) ? body.items : []);
      setPagination({
        total_records: Number(body.pagination?.total_records || 0),
        total_pages: Number(body.pagination?.total_pages || 0),
      });
    } catch (err) {
      setRecords([]);
      setError(
        err?.response?.data?.message ||
        err?.response?.data?.error ||
        err?.message ||
        "Unable to load Sales Invoice records."
      );
    } finally {
      setLoading(false);
    }
  }, [filters, page, perPage]);

  useEffect(() => {
    loadList();
  }, [loadList]);

  const loadKpis = useCallback(async () => {
    try {
      const count = async (statusCode) => {
        const params = { page: 1, per_page: 1 };
        if (statusCode) params.status_code = statusCode;

        const response =
          await tradingSalesInvoiceService.getSalesInvoiceList(params);

        return Number(
          getResponseData(response).pagination?.total_records || 0
        );
      };

      const [total, draft, pending, posted] = await Promise.all([
        count(""),
        count("DRAFT"),
        count("PENDING_APPROVAL"),
        count("POSTED"),
      ]);

      setKpis({ total, draft, pending, posted });
    } catch (err) {
      console.warn("Unable to load Sales Invoice KPI counts.", err);
    }
  }, []);

  useEffect(() => {
    loadKpis();
  }, [loadKpis]);

  const applyFilters = () => {
    if (fromDateInput && toDateInput && fromDateInput > toDateInput) {
      setError("From Date cannot be later than To Date.");
      return;
    }

    setPage(1);
    setFilters({
      search: searchInput.trim(),
      status_code: statusInput === "ALL" ? "" : statusInput,
      from_date: fromDateInput,
      to_date: toDateInput,
    });
  };

  const resetFilters = () => {
    setSearchInput("");
    setStatusInput("ALL");
    setFromDateInput("");
    setToDateInput("");
    setPage(1);
    setFilters({
      search: "",
      status_code: "",
      from_date: "",
      to_date: "",
    });
  };

  const openDetail = (row) => {
    if (row?.id) {
      navigate(`/trading/sales-invoices/${row.id}`);
    }
  };

  const totalPages = Math.max(1, pagination.total_pages || 1);
  const firstRecord = pagination.total_records
    ? (page - 1) * perPage + 1
    : 0;
  const lastRecord = Math.min(
    page * perPage,
    pagination.total_records
  );

  const renderActions = (row) => {
    const status = normalizeStatus(row.status_code);

    return (
      <div style={{ display: "flex", gap: 6 }}>
        <button
          type="button"
          style={styles.action}
          onClick={() => openDetail(row)}
        >
          View
        </button>

        {status === "DRAFT" && (
          <button
            type="button"
            style={styles.action}
            onClick={() =>
              navigate(`/trading/sales-invoices/${row.id}/edit`)
            }
          >
            Edit
          </button>
        )}
      </div>
    );
  };

  return (
    <div style={styles.page}>
      <div style={styles.header}>
        <div>
          <h1 style={styles.title}>Sales Invoice</h1>
          <p style={styles.subtitle}>
            Manage customer invoices generated from confirmed Delivery Challans.
          </p>
        </div>

        <button
          type="button"
          style={styles.button}
          onClick={() => navigate("/trading/sales-invoices/create")}
        >
          + Create Sales Invoice
        </button>
      </div>

      {error && (
        <div role="alert" style={styles.error}>
          {error}
          <button
            type="button"
            onClick={loadList}
            style={{ marginLeft: 12, border: 0, background: "transparent", color: "#b42318", cursor: "pointer" }}
          >
            Retry
          </button>
        </div>
      )}

      <div style={styles.stats}>
        <StatCard
          label="Total Invoices"
          value={kpis.total}
          accent={styles.total}
          icon="fas fa-file-invoice"
        />
        <StatCard
          label="Draft"
          value={kpis.draft}
          accent={styles.draft}
          icon="fas fa-file-alt"
        />
        <StatCard
          label="Pending Approval"
          value={kpis.pending}
          accent={styles.pending}
          icon="fas fa-clock"
        />
        <StatCard
          label="Posted"
          value={kpis.posted}
          accent={styles.posted}
          icon="fas fa-check-circle"
        />
      </div>

      <div style={styles.summary}>
        <div>
          <div style={{ fontSize: 12, fontWeight: 700, color: "#68718a" }}>
            Invoice Register Summary
          </div>
          <div style={{ fontSize: 11, color: "#858b9b", marginTop: 4 }}>
            Total invoices in the current tenant and location
          </div>
        </div>
        <strong style={{ fontSize: 21, color: "#3d45a5" }}>
          {kpis.total.toLocaleString("en-IN")} invoices
        </strong>
      </div>

      <div style={styles.card}>
        <div style={styles.filters}>
          <div style={styles.field}>
            <label style={styles.label}>Search</label>
            <input
              style={styles.input}
              value={searchInput}
              placeholder="Invoice number or customer..."
              onChange={(e) => setSearchInput(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && applyFilters()}
            />
          </div>

          <div style={styles.field}>
            <label style={styles.label}>Status</label>
            <select
              style={styles.input}
              value={statusInput}
              onChange={(e) => setStatusInput(e.target.value)}
            >
              <option value="ALL">All Statuses</option>
              <option value="DRAFT">Draft</option>
              <option value="PENDING_APPROVAL">Pending Approval</option>
              <option value="APPROVED">Approved</option>
              <option value="REJECTED">Rejected</option>
              <option value="POSTED">Posted</option>
              <option value="CANCELLED">Cancelled</option>
            </select>
          </div>

          <div style={styles.field}>
            <label style={styles.label}>From Date</label>
            <input
              type="date"
              style={styles.input}
              value={fromDateInput}
              onChange={(e) => setFromDateInput(e.target.value)}
            />
          </div>

          <div style={styles.field}>
            <label style={styles.label}>To Date</label>
            <input
              type="date"
              style={styles.input}
              value={toDateInput}
              onChange={(e) => setToDateInput(e.target.value)}
            />
          </div>

          <button
            type="button"
            style={styles.button}
            onClick={applyFilters}
          >
            Apply
          </button>

          <button
            type="button"
            style={styles.secondaryButton}
            onClick={resetFilters}
          >
            Reset
          </button>
        </div>

        <div style={styles.tableTop}>
          <div>
            <h3 style={{ margin: 0, fontSize: 15 }}>Invoice Register</h3>
            <div style={{ color: "#858b9b", fontSize: 11, marginTop: 4 }}>
              Scroll horizontally and vertically to view the register.
            </div>
          </div>

          <button
            type="button"
            style={styles.secondaryButton}
            onClick={() => {
              loadList();
              loadKpis();
            }}
            disabled={loading}
          >
            ↻ Refresh
          </button>
        </div>

        <div style={styles.tableWrap}>
          <table style={styles.table}>
            <thead>
              <tr>
                {[
                  "Invoice No.",
                  "Invoice Date",
                  "Customer",
                  "Sales Order",
                  "Taxable Amount",
                  "Total GST",
                  "Net Amount",
                  "Status",
                  "Actions",
                ].map((heading) => (
                  <th key={heading} style={styles.th}>
                    {heading}
                  </th>
                ))}
              </tr>
            </thead>

            <tbody>
              {loading ? (
                <tr>
                  <td colSpan={9} style={styles.muted}>
                    Loading Sales Invoice records...
                  </td>
                </tr>
              ) : records.length === 0 ? (
                <tr>
                  <td colSpan={9} style={styles.muted}>
                    No Sales Invoice records found.
                  </td>
                </tr>
              ) : (
                records.map((row) => (
                  <tr key={row.id}>
                    <td style={styles.td}>
                      <button
                        type="button"
                        style={styles.link}
                        onClick={() => openDetail(row)}
                      >
                        {row.invoice_no || `SI-${row.id}`}
                      </button>
                    </td>
                    <td style={styles.td}>
                      {formatDate(row.invoice_date)}
                    </td>
                    <td style={styles.td}>
                      {row.customer_name || "—"}
                    </td>
                    <td style={styles.td}>
                      {row.sales_order_no || "—"}
                    </td>
                    <td style={styles.td}>
                      ₹{formatAmount(row.taxable_amount)}
                    </td>
                    <td style={styles.td}>
                      ₹{formatAmount(row.total_tax_amount)}
                    </td>
                    <td style={{ ...styles.td, fontWeight: 750 }}>
                      ₹{formatAmount(row.net_amount)}
                    </td>
                    <td style={styles.td}>
                      <StatusBadge status={row.status_code} />
                    </td>
                    <td style={styles.td}>
                      {renderActions(row)}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        <div style={styles.footer}>
          <div>
            Showing {firstRecord}–{lastRecord} of{" "}
            {pagination.total_records.toLocaleString("en-IN")} invoices
          </div>

          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <label htmlFor="invoice-page-size">Rows</label>
            <select
              id="invoice-page-size"
              style={{ ...styles.input, width: 75, padding: "7px 8px" }}
              value={perPage}
              onChange={(e) => {
                setPerPage(Number(e.target.value));
                setPage(1);
              }}
            >
              {[10, 20, 50, 100].map((size) => (
                <option key={size} value={size}>{size}</option>
              ))}
            </select>

            <button
              type="button"
              style={styles.secondaryButton}
              disabled={page <= 1 || loading}
              onClick={() => setPage((current) => current - 1)}
            >
              Previous
            </button>

            <span>
              Page {page} of {totalPages}
            </span>

            <button
              type="button"
              style={styles.secondaryButton}
              disabled={page >= totalPages || loading}
              onClick={() => setPage((current) => current + 1)}
            >
              Next
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
